import * as THREE from 'three'
import {
    heightFogDefaultColor,
    heightFogDefaultDensity,
    heightFogDefaultEnabled,
    heightFogDefaultFalloff,
    heightFogDefaultFar,
    heightFogDefaultHeight,
    heightFogDefaultNear,
} from '../config/constants'

export const HEIGHT_FOG_SHARED_UNIFORMS = {
    uHeightFogEnabled: { value: heightFogDefaultEnabled ? 1 : 0 },
    uHeightFogColor: { value: new THREE.Color(heightFogDefaultColor) },
    uHeightFogDensity: { value: heightFogDefaultDensity },
    uHeightFogHeight: { value: heightFogDefaultHeight },
    uHeightFogFalloff: { value: heightFogDefaultFalloff },
    uHeightFogNear: { value: heightFogDefaultNear },
    uHeightFogFar: { value: heightFogDefaultFar },
}

export function applyHeightFogToMaterial(material: THREE.Material) {
    if (!material || (material.userData as Record<string, unknown>)?.heightFogApplied) return

    const previousOnBeforeCompile = material.onBeforeCompile
    const previousProgramCacheKey = material.customProgramCacheKey?.bind(material)

    material.userData.heightFogApplied = true

    material.onBeforeCompile = (shader, renderer) => {
        previousOnBeforeCompile?.(shader, renderer)

        shader.uniforms.uHeightFogEnabled = HEIGHT_FOG_SHARED_UNIFORMS.uHeightFogEnabled
        shader.uniforms.uHeightFogColor = HEIGHT_FOG_SHARED_UNIFORMS.uHeightFogColor
        shader.uniforms.uHeightFogDensity = HEIGHT_FOG_SHARED_UNIFORMS.uHeightFogDensity
        shader.uniforms.uHeightFogHeight = HEIGHT_FOG_SHARED_UNIFORMS.uHeightFogHeight
        shader.uniforms.uHeightFogFalloff = HEIGHT_FOG_SHARED_UNIFORMS.uHeightFogFalloff
        shader.uniforms.uHeightFogNear = HEIGHT_FOG_SHARED_UNIFORMS.uHeightFogNear
        shader.uniforms.uHeightFogFar = HEIGHT_FOG_SHARED_UNIFORMS.uHeightFogFar

        shader.vertexShader = shader.vertexShader
            .replace('#include <common>', `#include <common>
varying vec3 vHeightFogWorldPosition;
varying float vHeightFogDepth;`)
            .replace('#include <project_vertex>', `#include <project_vertex>
vHeightFogWorldPosition = ( modelMatrix * vec4( transformed, 1.0 ) ).xyz;
vHeightFogDepth = - mvPosition.z;`)

        shader.fragmentShader = shader.fragmentShader
            .replace('#include <common>', `#include <common>
varying vec3 vHeightFogWorldPosition;
varying float vHeightFogDepth;
uniform float uHeightFogEnabled;
uniform vec3 uHeightFogColor;
uniform float uHeightFogDensity;
uniform float uHeightFogHeight;
uniform float uHeightFogFalloff;
uniform float uHeightFogNear;
uniform float uHeightFogFar;`)
            .replace('#include <fog_fragment>', `#include <fog_fragment>
if (uHeightFogEnabled > 0.5) {
  float heightAttenuation = exp(-max(vHeightFogWorldPosition.y - uHeightFogHeight, 0.0) * max(uHeightFogFalloff, 0.0001));
  float baseDensity = max(uHeightFogDensity, 0.0);
  float heightFogDensity = baseDensity * heightAttenuation;
  float heightFogFactor = 1.0 - exp(-heightFogDensity * heightFogDensity * vHeightFogDepth * vHeightFogDepth);
  float distanceSpan = max(uHeightFogFar - uHeightFogNear, 0.0001);
  float distanceDepth = clamp(vHeightFogDepth - uHeightFogNear, 0.0, distanceSpan);
  float distanceFogFactor = 1.0 - exp(-baseDensity * baseDensity * distanceDepth * distanceDepth);
  distanceFogFactor *= smoothstep(uHeightFogNear, uHeightFogFar, vHeightFogDepth);
  float fogFactor = 1.0 - (1.0 - clamp(heightFogFactor, 0.0, 1.0)) * (1.0 - clamp(distanceFogFactor, 0.0, 1.0));
  gl_FragColor.rgb = mix(gl_FragColor.rgb, uHeightFogColor, clamp(fogFactor, 0.0, 1.0));
}`)
    }

    material.customProgramCacheKey = () =>
        `${previousProgramCacheKey ? previousProgramCacheKey() : material.type}|height-fog-v1`
}

export function applyHeightFogToObject(root: THREE.Object3D) {
    root.traverse((child) => {
        if (!(child instanceof THREE.Mesh)) {
            return
        }

        const materials = Array.isArray(child.material)
            ? child.material
            : [child.material]

        for (const material of materials) {
            applyHeightFogToMaterial(material)
        }
    })
}

export function updateHeightFogSettings({
    enabled,
    color,
    density,
    height,
    falloff,
    near,
    far,
}: {
    enabled: boolean
    color: THREE.ColorRepresentation
    density: number
    height: number
    falloff: number
    near: number
    far: number
}) {
    HEIGHT_FOG_SHARED_UNIFORMS.uHeightFogEnabled.value = enabled ? 1 : 0
    HEIGHT_FOG_SHARED_UNIFORMS.uHeightFogColor.value.set(color)
    HEIGHT_FOG_SHARED_UNIFORMS.uHeightFogDensity.value = Math.max(0, density)
    HEIGHT_FOG_SHARED_UNIFORMS.uHeightFogHeight.value = height
    HEIGHT_FOG_SHARED_UNIFORMS.uHeightFogFalloff.value = Math.max(0.001, falloff)
    HEIGHT_FOG_SHARED_UNIFORMS.uHeightFogNear.value = Math.max(0, Math.min(near, far - 0.01))
    HEIGHT_FOG_SHARED_UNIFORMS.uHeightFogFar.value = Math.max(
        HEIGHT_FOG_SHARED_UNIFORMS.uHeightFogNear.value + 0.01,
        far,
    )
}

export function setHeightFogColor(color: THREE.ColorRepresentation) {
    HEIGHT_FOG_SHARED_UNIFORMS.uHeightFogColor.value.set(color)
}

export function lerpHeightFogColor(
    fromColor: THREE.ColorRepresentation,
    toColor: THREE.ColorRepresentation,
    progress: number,
) {
    const from = new THREE.Color(fromColor)
    const to = new THREE.Color(toColor)

    HEIGHT_FOG_SHARED_UNIFORMS.uHeightFogColor.value.lerpColors(
        from,
        to,
        THREE.MathUtils.clamp(progress, 0, 1),
    )
}
